// TabHandleStrip — the `role="tablist"` container for {@link TabHandle}s.
//
// Implements the ARIA tabs keyboard pattern the hand-rolled rails were missing:
// ←/→ move between handles and Home/End jump to the ends, wrapping and skipping
// disabled handles. The strip is a single tab stop because {@link TabHandle}
// carries the roving `tabIndex` (0 when active, -1 otherwise).
//
// The strip only owns focus mechanics; selection stays with the caller (each
// handle's own `onClick`). Arrow keys MOVE focus, they don't select — the
// caller's handle still needs Enter/Space, which a <button> gives for free.
import { useRef, type KeyboardEvent, type ReactNode } from "react";
import { cx } from "./classNames.js";

export interface TabHandleStripProps {
  /** Accessible name for the tablist (e.g. "Navigator"). */
  readonly label: string;
  readonly className?: string;
  readonly children: ReactNode;
}

/** Every enabled handle in DOM order. */
function handlesOf(strip: HTMLElement): HTMLButtonElement[] {
  return Array.from(strip.querySelectorAll<HTMLButtonElement>('[role="tab"]')).filter(
    (el) => !el.disabled,
  );
}

export function TabHandleStrip({ label, className, children }: TabHandleStripProps) {
  const ref = useRef<HTMLDivElement>(null);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    const strip = ref.current;
    if (!strip) return;
    const tabs = handlesOf(strip);
    if (tabs.length === 0) return;
    const from = tabs.indexOf(document.activeElement as HTMLButtonElement);
    let next: number;
    switch (e.key) {
      case "ArrowRight":
        next = from < 0 ? 0 : (from + 1) % tabs.length;
        break;
      case "ArrowLeft":
        next = from < 0 ? tabs.length - 1 : (from - 1 + tabs.length) % tabs.length;
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = tabs.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    tabs[next]?.focus();
  };

  return (
    <div
      ref={ref}
      role="tablist"
      aria-label={label}
      onKeyDown={onKeyDown}
      className={cx(className)}
    >
      {children}
    </div>
  );
}
